import React, { useMemo } from 'react';

interface MemoizedIframePreviewProps {
  url: string;
  title: string;
  assumePdf?: boolean;
}

const withPdfViewerParams = (url: string, assumePdf = false): string => {
  try {
    const urlObj = new URL(url, window.location.origin);
    const pathname = urlObj.pathname.toLowerCase();
    const isPdf =
      assumePdf ||
      pathname.endsWith('.pdf') ||
      urlObj.searchParams.get('format') === 'pdf' ||
      urlObj.searchParams.get('content_type') === 'application/pdf';

    if (!isPdf) return url;

    return `${url}#view=FitH&toolbar=1&navpanes=0`;
  } catch {
    return url;
  }
};

/**
 * Helper to normalize URL by removing query params
 * This prevents iframe re-renders when only query params change
 */
const stripQueryParams = (url: string): string => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    return url;
  }
};

/**
 * Memoized iframe component that only re-renders when the base URL changes
 * Query params are ignored to prevent flickering during polling
 */
const MemoizedIframePreviewComponent: React.FC<MemoizedIframePreviewProps> = ({
  url,
  title,
  assumePdf = false,
}) => {
  const normalizedUrl = useMemo(() => stripQueryParams(url), [url]);
  const iframeUrl = useMemo(
    () => withPdfViewerParams(url, assumePdf),
    [assumePdf, url],
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <iframe
        key={normalizedUrl}
        src={iframeUrl}
        className="block h-full w-full min-h-0 flex-1 rounded-lg"
        title={title}
      />
    </div>
  );
};

export const MemoizedIframePreview = React.memo(
  MemoizedIframePreviewComponent,
  (prev, next) => {
    const prevNormalized = stripQueryParams(prev.url);
    const nextNormalized = stripQueryParams(next.url);
    return (
      prevNormalized === nextNormalized &&
      prev.title === next.title &&
      prev.assumePdf === next.assumePdf
    );
  },
);

MemoizedIframePreview.displayName = 'MemoizedIframePreview';
